import { Badge, Tooltip } from '@alphonsocode/kallos';
import React from 'react';
import { get } from 'lodash';
import { FieldMapping } from 'containers/NewCreativePreview/constants';

const CARD_ROW_FONT_SIZE = 13;

function normalizeTvServerEnvironment(value: unknown) {
  const normalized = String(value || '')
    .replaceAll('"', '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

  if (normalized === 'production' || normalized === 'prod') {
    return 'production';
  }

  if (normalized === 'qa2') {
    return 'qa2';
  }

  return null;
}

function getTestMosaicUrl({
  data,
  mosaicUrl,
  mosaicStagingUrl,
}: {
  data: any;
  mosaicUrl?: string | null;
  mosaicStagingUrl?: string | null;
}) {
  const productionMosaicUrl = mosaicUrl?.replace(/\/+$/, '');
  const stagingMosaicUrl = mosaicStagingUrl?.replace(/\/+$/, '');
  const testEnvironment = normalizeTvServerEnvironment(
    data?.tvServerEnvironment ||
      get(data, 'testMetadata.tvServerEnvironment') ||
      get(data, 'testMetadata.server') ||
      data?.server,
  );

  if (testEnvironment === 'production') {
    return productionMosaicUrl;
  }

  if (testEnvironment === 'qa2') {
    return stagingMosaicUrl || productionMosaicUrl;
  }

  return productionMosaicUrl;
}

export function FirstRow({
  data,
  testType,
  mosaicUrl,
  mosaicStagingUrl,
}) {
  const statusStyles = {
    'Not Picked': {
      backgroundColor: '#efe5fa',
      color: '#8d40a8',
    },
    'Unsupported Test': {
      backgroundColor: '#efefef',
      color: '#6b6b6b',
    },
    'In Progress': {
      backgroundColor: '#ffe9c8',
      color: '#d08200',
    },
    'Test Completed': {
      backgroundColor: '#d8f0f2',
      color: '#137f95',
    },
    Error: {
      backgroundColor: '#fde8e8',
      color: '#b70303',
    },
    'Rendering Verified': {
      backgroundColor: '#d8f0f2',
      color: '#137f95',
    },
    'CTA Verified': {
      backgroundColor: '#d8f0f2',
      color: '#137f95',
    },
    'Rendering Failed': {
      backgroundColor: '#fde8e8',
      color: '#b70303',
    },
    'CTA Failed': {
      backgroundColor: '#fde8e8',
      color: '#b70303',
    },
  };
  const labels = {
    acrids: 'ACRID',
    acid: 'ACID',
    runId: 'Run ID',
    supplier: 'Supplier',
    oppId: 'Opp ID',
    adomain: 'Ad Domain',
    osv: 'OS',
    country: 'Country',
    adtype: 'Ad Type',
    adslot: 'AdSlot',
    assetLabel: 'Asset',
    createdBy: 'Created By',
  };
  const campaignId = get(data, FieldMapping.acid, '-');
  const testMosaicUrl = getTestMosaicUrl({
    data,
    mosaicUrl,
    mosaicStagingUrl,
  });

  const statusColor = {
    'Not Picked': 'lgLightPurple',
    Error: 'info',
    'In Progress': 'warning',
    'Unsupported Test': 'lgGray',
    'Test Completed': 'lgDarkGreen',
    Unknown: 'info',
    'CTA Verified': 'lgDarkGreen',
    'Rendering Verified': 'lgDarkGreen',
    'Rendering Failed': 'danger',
    'CTA Failed': 'danger',
  };
  const widths = {
    acrids: testType === 'on-demand' ? '0.8 1 110px' : '0 0 15%',
    acid: testType === 'on-demand' ? '0.8 1 110px' : '0 0 10%',
    runId: '0 0 10%',
    supplier: '0 0 11%',
    oppId: '0 0 14%',
    adomain: '0 0 14%',
    osv: testType === 'on-demand' ? '1 1 125px' : '0 0 11%',
    country: testType === 'on-demand' ? '0.9 1 110px' : '0 0 10%',
    adtype: testType === 'on-demand' ? '1 1 125px' : '0 0 16%',
    adslot: testType === 'on-demand' ? '1.8 1 220px' : '0 0 22%',
    assetLabel: '1.4 1 170px',
    createdBy: testType === 'on-demand' ? '1.6 1 180px' : '0 0 17%',
  };
  const status = get(data, 'status', 'Unknown');
  const statusStyle = statusStyles[status] || {};
  const shouldWrapFirstRow = testType === 'on-demand' || testType === 'ssp';
  const displayedFields =
    testType === 'ssp'
      ? [
          ['acrids', FieldMapping.acrids],
          ['adtype', FieldMapping.adtype],
          ['runId', 'external_run_id'],
          ['supplier', 'supplier_name'],
          ['oppId', 'opp_id'],
          ['adomain', 'adomain'],
          ['adslot', FieldMapping.adslot],
          ['osv', FieldMapping.osv],
          ['country', FieldMapping.country],
        ]
      : testType === 'on-demand' &&
          (data.parentTestId || get(data, 'testMetadata.parentTestId'))
        ? [
            ['assetLabel', 'assetLabel'],
            ...Object.entries(FieldMapping),
          ]
        : Object.entries(FieldMapping);

  return (
    <div
      className="row first-row w-80 mx-auto g-0 align-items-center"
      style={{
        marginLeft: 0,
        marginRight: 0,
        paddingLeft: 8,
        paddingRight: 8,
        textAlign: 'left',
        fontFamily: 'lg-smart, sans-serif',
        fontSize: CARD_ROW_FONT_SIZE,
        fontWeight: 400,
        lineHeight: 1.25,
      }}
    >
      <div
        className="col"
        style={{
          flex: '0 0 20%',
          color: '#852828',
          fontFamily: 'lg-smart, sans-serif',
          fontWeight: 'bold',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>Status</span>
        <Badge
          pill
          variant={statusColor[status]}
          style={{
            fontFamily: 'lg-smart, sans-serif',
            width: 'auto',
            fontWeight: 600,
            marginLeft: 15,
            fontSize: '10.5px',
            padding: '5px 12px',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            ...statusStyle,
          }}
        >
          {status}
        </Badge>
      </div>

      <div
        className="col d-flex align-items-center"
        style={{
          flex: '0 0 80%',
          gap: shouldWrapFirstRow ? '8px' : '12px',
          flexWrap: shouldWrapFirstRow ? 'wrap' : 'nowrap',
          minWidth: 0,
          overflow: testType === 'ssp' ? 'visible' : 'hidden',
        }}
      >
        {displayedFields.map(([key, path]) => {
          if (
            (key === 'acrids' || key === 'acid') &&
            data.userFlow === 'manual'
          )
            return null;
          if (
            (key === 'acrids' || key === 'acid' || key === 'adtype') &&
            data.userFlow === 'webTester'
          )
            return null;
          let value = get(data, path, '-');
          if (key === 'createdBy' && testType !== 'on-demand') return null;
          if (key === 'osv') {
            const make = data.make;
            if (make === 'LG') value = 'WebOS ' + value;
            else if (make === 'WeeTV') value = 'WeeTV ' + value;
          }
          const tooltipValue = String(value ?? '');
          const shouldShowTooltip = tooltipValue !== '-';
          const hasMosaicLink = Boolean(
            testType !== 'ssp' && testMosaicUrl && campaignId !== '-',
          );
          const displayValue =
            key === 'acid' && hasMosaicLink ? (
              <a
                href={`${testMosaicUrl}/campaign/overview/${campaignId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007bff', textDecoration: 'underline' }}
              >
                {value}
              </a>
            ) : key === 'acrids' && hasMosaicLink ? (
              <a
                href={`${testMosaicUrl}/campaign/overview/${campaignId}?tab=creative`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#007bff', textDecoration: 'underline' }}
              >
                {value}
              </a>
            ) : (
              value
            );
          const content = (
            <span
              style={{
                marginLeft: 5,
                fontWeight: 400,
                color: '#000000',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {displayValue}
            </span>
          );

          return (
            <div
              key={key}
              style={{
                flex: widths[key],
                fontFamily: 'lg-smart, sans-serif',
                minWidth: 0,
              }}
              className="d-flex align-items-center"
            >
              <label
                style={{
                  fontWeight: 'bold',
                  marginTop: 0,
                  marginBottom: 0,
                  color: '#000000',
                  flexShrink: 0,
                }}
              >
                {labels[key]}:
              </label>

              {shouldShowTooltip ? (
                <Tooltip
                  overlay={tooltipValue}
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  openDelay={0}
                  closeDelay={0}
                  arrow={false}
                  style={{
                    maxWidth: '300px',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {content}
                </Tooltip>
              ) : (
                content
              )}
            </div>
          );
        })}
      </div>

      <hr
        style={{
          marginLeft: 0,
          marginRight: 0,
          marginTop: 6,
          marginBottom: 0,
          height: '2px',
          border: 'none',
          width: '100%',
        }}
      />
    </div>
  );
}
